/**
 * Voxly — Capacitor native bridge.
 *
 * Hot path:
 *   import { initNative, isNative } from "@/lib/native";
 *   initNative();           // call once at app boot
 *   if (isNative()) ...     // gate platform-specific UX
 *
 * What this does on a native shell (iOS / Android):
 *   - Hides the splash screen as soon as the React app mounts
 *   - Configures status bar to dark style + brand bg color
 *   - Wires the Android hardware back button to react-router history
 *   - Listens for `voxly://auth/magic?token=…` deep links and routes them in
 *
 * On the regular web build, all of these are no-ops.
 */
import { Capacitor } from "@capacitor/core";

let _initialized = false;

export const isNative = () => Capacitor.isNativePlatform?.() === true;
export const platform = () => Capacitor?.getPlatform?.() || "web";

export async function initNative(navigate) {
  if (_initialized) return;
  _initialized = true;
  if (!isNative()) return;

  // Status bar — orange/dark theme to match the app shell.
  try {
    const { StatusBar, Style } = await import("@capacitor/status-bar");
    await StatusBar.setStyle({ style: Style.Dark });
    if (platform() === "android") {
      await StatusBar.setBackgroundColor({ color: "#0E0E10" });
    }
    await StatusBar.setOverlaysWebView({ overlay: false });
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn("[native] StatusBar setup failed", e);
  }

  // Hide the splash screen now that React has mounted.
  try {
    const { SplashScreen } = await import("@capacitor/splash-screen");
    await SplashScreen.hide();
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn("[native] SplashScreen.hide failed", e);
  }

  // Android back button → browser history.back()
  try {
    const { App } = await import("@capacitor/app");
    App.addListener("backButton", ({ canGoBack }) => {
      if (canGoBack) {
        window.history.back();
      } else {
        // On root screens, exit the app.
        App.exitApp();
      }
    });

    // Magic-link deep links: voxly://auth/magic?token=…
    App.addListener("appUrlOpen", (event) => {
      try {
        const url = new URL(event.url);
        // Convert custom-scheme URL into an in-app route.
        // voxly://auth/magic?token=... → /auth/magic?token=...
        const path = url.pathname || "";
        const search = url.search || "";
        const target = `${path}${search}`;
        if (navigate) navigate(target, { replace: true });
        else window.location.href = target;
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn("[native] bad deep link", event.url, e);
      }
    });
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn("[native] App listener setup failed", e);
  }
}

/** Trigger a light haptic tick on supported native shells; no-op on web. */
export async function haptic(kind = "light") {
  if (!isNative()) return;
  try {
    const { Haptics, ImpactStyle } = await import("@capacitor/haptics");
    const style =
      kind === "heavy" ? ImpactStyle.Heavy :
      kind === "medium" ? ImpactStyle.Medium :
      ImpactStyle.Light;
    await Haptics.impact({ style });
  } catch {
    /* noop */
  }
}

/**
 * Native share — falls back to navigator.share, then noop.
 * Returns true if shared, false otherwise.
 */
export async function nativeShare({ title, text, url }) {
  if (isNative()) {
    try {
      const { Share } = await import("@capacitor/share");
      const can = await Share.canShare();
      if (can?.value) {
        await Share.share({ title, text, url });
        return true;
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("[native] Share failed", e);
    }
  }
  if (typeof navigator !== "undefined" && navigator.share) {
    try {
      await navigator.share({ title, text, url });
      return true;
    } catch {
      return false;
    }
  }
  return false;
}
